import React, { useMemo, useState } from "react";
import { AxiosError } from "axios";
import { QueryKey, queryCache, useMutation, useQuery } from "react-query";
import { useDebounce } from "use-debounce";
import { useTranslation } from "react-i18next";
import { useHistory, useRouteMatch } from "react-router-dom";

import {
  Dialog,
  Loader,
  TextInput,
  MultiSelectInput,
  ApiErrorBanner,
  showNotification,
} from "fe-shared/src/components";
// The barrel's FileUploader is the container that uploads to a draft path on drop. A pickup
// request document can only be attached once the request exists, so the presentational
// component is used directly and the upload runs after create.
import FileUploader from "fe-shared/src/components/FileUploader/FileUploader";
import { CloseIcon } from "fe-shared/src/components/Icons";
import useRecycleMaterials from "fe-shared/src/hooks/useRecycleMaterials";
import {
  ApiError,
  FileInterface,
  FileRejection,
  FileStatus,
} from "fe-shared/src/types/common";
import {
  CreatePickupRequest,
  ParticipantOption,
  PickupRequestDetails,
  PickupRequestDocument,
  PickupRequestFormValues,
  PickupRequestPriorityId,
} from "../../types";
import { MATERIAL_ROLES } from "../../consts";
import { getPickupRequestCarriers } from "../../api/pickupRequestCarriers";
import { getPickupRequestHaulers } from "../../api/pickupRequestHaulers";
import {
  createPickupRequest,
  getPickupRequestCollectors,
  getPickupRequestReasons,
  uploadPickupRequestDocument,
  deleteDocument,
  getRequest,
  updatePickupRequest,
} from "../../api/pickupRequests";


import PickupRequestForm, {
  CarrierOption,
  HaulerOption,
} from "../PickupRequestForm";

import {
  ALLOWED_DOCUMENT_EXTENSIONS,
  DocumentRejectionReason,
  PendingDocument,
  partitionSelectedDocuments,
  describeDropzoneRejections,
  toUploaderFiles,
} from "./modules/documents";
import { toCreatePickupRequest, toFormValues, toUpdatePickupRequest } from "./modules/mapper";
import {
  isOngoingPickupError,
  mapApiErrorToServerErrors,
} from "./modules/errors";
import {
  OTHER_REASON_MAX_LENGTH,
  isOtherReasonRequired,
  validateOtherReasonText,
} from "./modules/reasons";
import {
  SubmitPickupRequestEditResult,
  SubmitPickupRequestResult,
  submitPickupRequest,
  submitPickupRequestEdit,
} from "./modules/submit";

import styles from "./PickupRequestDialog.module.scss";

export interface Props {
  requestsQueryKey: QueryKey;
  isActive: boolean;
  mode: "create" | "edit";
  /** Edit mode only: the details response supplying the prefill and the locked collector's name. */
  request?: PickupRequestDetails;
  /** Edit mode only: the request's own number - not read off `request` since it is nullable there. */
  requestNumber?: string;
  /** Edit mode only: already-attached documents, offered for staged removal. */
  existingDocuments?: PickupRequestDocument[];
  /** Gates the documents dropzone and any removal control - defaults to true for create mode. */
  canManageDocuments?: boolean;
  /** Edit mode only: false hides the removal control on existing documents - defaults to true. */
  canDeleteDocuments?: boolean;
  /** Skips the collectors query and seeds the form with this collector; `mode="edit"` still
   *  takes its collector from `request` instead. */
  lockedCollector?: ParticipantOption;
  /** Hauler, carrier and reasons are portal-only - defaults to true. */
  showPortalOnlyFields?: boolean;
  /** Reasons block visibility - defaults to showPortalOnlyFields, so a hauler can edit reasons
   *  while hauler, carrier and priority stay hidden. */
  showReasons?: boolean;
  onClose: () => void;
  /** Fired after a successful submit, in addition to the dialog's own query invalidation. */
  onSuccess?: () => void;
}

const EMPTY_FORM_VALUES: PickupRequestFormValues = {
  collectorId: null,
  haulerId: null,
  carrierId: null,
  materials: [],
  pickupDateFrom: null,
  pickupDateTo: null,
  comment: null,
  priority: PickupRequestPriorityId.Low,
  priorityReason: null,
};

// PickupRequestForm keys effects on these arrays, so the no-data render has to hand back the
// same reference every time or the effects loop.
const EMPTY_CARRIER_OPTIONS: CarrierOption[] = [];
const EMPTY_HAULER_OPTIONS: HaulerOption[] = [];

const NO_UPLOAD_PROGRESS = {};

const COLLECTOR_SEARCH_DEBOUNCE_MS = 400;

const DOCUMENT_REJECTION_KEYS: Record<DocumentRejectionReason, string> = {
  extension: "pickupRequests.createDialog.documentRejectedExtension",
  size: "pickupRequests.createDialog.documentRejectedSize",
  totalSize: "pickupRequests.createDialog.documentsRejectedTotalSize",
};

const EXISTING_DOCUMENT_ID_PREFIX = "existing-";

const PickupRequestDialog = (props: Props) => {
  const {
    requestsQueryKey,
    isActive,
    mode,
    request,
    requestNumber,
    existingDocuments,
    canManageDocuments = true,
    canDeleteDocuments = true,
    lockedCollector,
    showPortalOnlyFields = true,
    showReasons = showPortalOnlyFields,
    onClose,
    onSuccess,
  } = props;
  const isEdit = mode === "edit";
  const isCollectorLocked = isEdit || !!lockedCollector;
  const isAssignmentLocked = isEdit && !!request;

  const { t } = useTranslation();
  const history = useHistory();
  const match = useRouteMatch();

  const [collectorSearchText, setCollectorSearchText] = useState("");
  const [debouncedCollectorSearch] = useDebounce(
    collectorSearchText.trim(),
    COLLECTOR_SEARCH_DEBOUNCE_MS
  );

  // Fetched with an empty search too: the field renders as a drop-down, so opening it has to
  // offer something. The procedure caps the result at the limit and orders by name. Skipped in
  // edit mode - the collector is locked, so there is nothing to search for. Skipped for a
  // locked collector for the same reason.
  const collectors = useQuery(
    ["pickupRequestCollectors", debouncedCollectorSearch],
    getPickupRequestCollectors,
    {
      enabled: !isEdit && !lockedCollector,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const collectorOptions: ParticipantOption[] = useMemo(() => {
    if (isEdit) {
      return request?.source
        ? [
            {
              companyId: request.source.companyId,
              displayName: request.source.displayName ?? "",
              registrationNumber: request.source.registrationNumber ?? undefined,
              addressLine1: request.source.addressLine1 ?? undefined,
              city: request.source.city ?? undefined,
            },
          ]
        : [];
    }

    if (lockedCollector) {
      return [lockedCollector];
    }

    return (collectors.data ?? []).map((collector) => ({
      companyId: collector.companyId,
      displayName: collector.displayName,
      regNumber: collector.regNumber,
      registrationNumber: collector.registrationNumber,
      addressLine1: collector.addressLine1 ?? undefined,
      city: collector.city ?? undefined,
    }));
  }, [isEdit, request, lockedCollector, collectors.data]);

  // materials/recycleMaterials returns one row per material *per role set*, so a code can come
  // back more than once - deduped here or the same tire type renders twice in the checkbox list.
  const { data: recycleMaterials, isLoading: isMaterialsLoading } =
    useRecycleMaterials(MATERIAL_ROLES);
  const materialTypes = useMemo(() => {
    const byCode = new Map<
      string,
      { code: string; name: string; standardWeight: number }
    >();

    (recycleMaterials ?? []).forEach(({ code, name, standardWeight }) => {
      if (!byCode.has(code)) {
        byCode.set(code, { code, name, standardWeight });
      }
    });

    return Array.from(byCode.values());
  }, [recycleMaterials]);

  const reasonsQuery = useQuery(
    "pickupRequestReasons",
    getPickupRequestReasons,
    { enabled: showReasons }
  );
  const reasons = useMemo(() => reasonsQuery.data ?? [], [reasonsQuery.data]);
  const reasonOptions = useMemo(
    () =>
      reasons.map((reason) => ({
        value: reason.reasonId,
        label: reason.reasonName,
      })),
    [reasons]
  );

  const [formValues, setFormValues] = useState(() => {
    if (isEdit && request) return toFormValues(request);
    if (lockedCollector) {
      return { ...EMPTY_FORM_VALUES, collectorId: lockedCollector.companyId };
    }
    return EMPTY_FORM_VALUES;
  });
  const [reasonIds, setReasonIds] = useState<number[]>(
    () => (isEdit && request ? request.reasonIds : [])
  );
  const [otherReasonText, setOtherReasonText] = useState(
    () => (isEdit && request ? request.otherReasonText ?? "" : "")
  );
  const [otherReasonError, setOtherReasonError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<PendingDocument[]>([]);
  const [documentErrors, setDocumentErrors] = useState<string[]>([]);
  const [removedDocumentIds, setRemovedDocumentIds] = useState<number[]>([]);

  const haulersQuery = useQuery(
    ["pickupRequestHaulers", formValues.collectorId],
    () => getPickupRequestHaulers(formValues.collectorId as number),
    {
      enabled: !!formValues.collectorId && showPortalOnlyFields && !isAssignmentLocked,
      refetchOnWindowFocus: false,
    }
  );
  const haulerOptions = haulersQuery.data ?? EMPTY_HAULER_OPTIONS;

  const carriersQuery = useQuery(
    ["pickupRequestCarriers", formValues.haulerId],
    () => getPickupRequestCarriers(formValues.haulerId as number),
    {
      enabled: !!formValues.haulerId && showPortalOnlyFields && !isAssignmentLocked,
      refetchOnWindowFocus: false,
      onError: () => {
        showNotification(
          t("pickupRequests.details.carriersLoadError"),
          undefined,
          CloseIcon
        );
      },
    }
  );
  const carrierOptions = carriersQuery.data ?? EMPTY_CARRIER_OPTIONS;

  const isOtherReasonSelected = isOtherReasonRequired(reasonIds, reasons);
  const isOtherReasonMissing = isOtherReasonSelected && !otherReasonText.trim();

  // MultiSelectInput always renders its placeholder when closed, so the selection has to be
  // written into it or the field keeps reading "Select" after picking something.
  const reasonsPlaceholder = useMemo(() => {
    if (!reasonIds.length) {
      return t("pickupRequests.createDialog.reasonsPlaceholder");
    }

    if (reasonIds.length === 1) {
      const selected = reasons.find(
        (reason) => reason.reasonId === reasonIds[0]
      );

      return (
        selected?.reasonName ?? t("pickupRequests.createDialog.reasonsMany")
      );
    }

    return t("pickupRequests.createDialog.reasonsMany");
  }, [reasonIds, reasons, t]);

  const [createSubmit, createSubmitResult] = useMutation<
    SubmitPickupRequestResult,
    AxiosError<ApiError>,
    { payload: CreatePickupRequest; documents: PendingDocument[] }
  >(
    (variables) =>
      submitPickupRequest(variables.payload, variables.documents, {
        createPickupRequest,
        uploadPickupRequestDocument,
      }),
    {
      onSuccess: (result) => {
        queryCache.invalidateQueries(requestsQueryKey);

        if (result.failedUploads.length) {
          showNotification(
            t("pickupRequests.createDialog.documentsUploadPartialError", {
              count: result.failedUploads.length,
            }),
            undefined,
            CloseIcon
          );
        } else {
          showNotification(t("pickupRequests.createDialog.submitSuccess"));
        }

        onClose();
        onSuccess?.();

        // Create answers with the number alone; the details page reads the request in full.
        history.push(`${match.url}/${result.requestNumber}`);
      },
    }
  );

  const [editSubmit, editSubmitResult] = useMutation<
    SubmitPickupRequestEditResult,
    AxiosError<ApiError>,
    { payload: ReturnType<typeof toUpdatePickupRequest> }
  >(
    async ({ payload }) => {
      const current = isAssignmentLocked
        ? await getRequest(requestNumber as string)
        : null;

      return submitPickupRequestEdit(
        requestNumber as string,
        current
          ? {
              ...payload,
              targetCompanyId: current.targetCompanyId,
              assignedCarrierId: current.assignedCarrierId,
            }
          : payload,
        documents,
        removedDocumentIds,
        { updatePickupRequest, uploadPickupRequestDocument, deleteDocument }
      );
    },
    {
      onSuccess: (result) => {
        queryCache.invalidateQueries(requestsQueryKey);

        if (result.failedUploads.length || result.failedDeleteDocumentIds.length) {
          showNotification(
            t("pickupRequests.createDialog.documentsUploadPartialError", {
              count:
                result.failedUploads.length +
                result.failedDeleteDocumentIds.length,
            }),
            undefined,
            CloseIcon
          );
        } else {
          showNotification(t("pickupRequests.createDialog.editSuccess"));
        }

        onClose();
        onSuccess?.();
      },
    }
  );

  const submitResult = isEdit ? editSubmitResult : createSubmitResult;

  const fieldServerErrors = useMemo(() => {
    const apiError = submitResult.error?.response?.data;

    // Only divert to the collector field when it is rendered - a locked collector has no
    // field to carry the error, so it must fall through to the generic banner instead.
    if (isOngoingPickupError(apiError) && !isCollectorLocked) {
      return { collectorId: t("pickupRequests.errors.ongoingPickup") };
    }

    return mapApiErrorToServerErrors(apiError);
  }, [submitResult.error, t, isCollectorLocked]);

  const handleReasonsChange = (
    _event: React.SyntheticEvent,
    values: (string | number)[]
  ) => {
    const nextReasonIds = values as number[];

    setReasonIds(nextReasonIds);
    if (!isOtherReasonRequired(nextReasonIds, reasons)) {
      setOtherReasonText("");
      setOtherReasonError(null);
    }
  };

  // Sizes of already-attached documents are not counted against the client-side total-size
  // filter below - the server enforces the real cap on upload, so this only means a batch that
  // is over cap by an already-attached document's size gets a server-side rejection instead of
  // a friendlier client-side one.
  const visibleExistingDocuments = useMemo(
    () =>
      (existingDocuments ?? []).filter(
        (document) => !removedDocumentIds.includes(document.documentId)
      ),
    [existingDocuments, removedDocumentIds]
  );

  const uploadedFiles: FileInterface[] = useMemo(
    () => [
      ...visibleExistingDocuments.map((document) => ({
        id: `${EXISTING_DOCUMENT_ID_PREFIX}${document.documentId}`,
        name: document.name,
        size: document.size,
        description: document.description ?? "",
        status: FileStatus.Loaded,
        isRemoveForbidden:
          !canManageDocuments || !canDeleteDocuments || !document.isDeletable,
      })),
      ...toUploaderFiles(documents),
    ],
    [visibleExistingDocuments, documents, canManageDocuments, canDeleteDocuments]
  );

  const handleFilesDropped = (files: File[], rejections: FileRejection[] = []) => {
    const { accepted, rejected } = partitionSelectedDocuments(files, documents);
    const refused = [...rejected, ...describeDropzoneRejections(rejections)];

    if (!accepted.length && !refused.length) {
      return;
    }

    setDocuments((prev) => [...prev, ...accepted]);
    setDocumentErrors(
      refused.map(
        (item) =>
          `${item.file.name}: ${t(DOCUMENT_REJECTION_KEYS[item.reason])}`
      )
    );
  };

  const removeDocument = (id: string) => {
    if (id.startsWith(EXISTING_DOCUMENT_ID_PREFIX)) {
      const documentId = Number(id.slice(EXISTING_DOCUMENT_ID_PREFIX.length));

      setRemovedDocumentIds((prev) => [...prev, documentId]);
      return;
    }

    setDocuments((prev) => prev.filter((document) => document.id !== id));
  };

  const changeDocumentDescription = (id: string, description: string) => {
    if (id.startsWith(EXISTING_DOCUMENT_ID_PREFIX)) {
      return;
    }

    setDocuments((prev) =>
      prev.map((document) =>
        document.id === id ? { ...document, description } : document
      )
    );
  };

  const handleOtherReasonBlur = () => {
    const validationKey = validateOtherReasonText(
      reasonIds,
      otherReasonText,
      reasons
    );

    setOtherReasonError(validationKey ? t(validationKey) : null);
  };

  const handleSubmit = (values: PickupRequestFormValues) => {
    if (isEdit) {
      if (request) {
        editSubmit({
          payload: toUpdatePickupRequest(values, {
            reasonIds,
            otherReasonText,
          }),
        });
      }

      return;
    }

    createSubmit({
      payload: toCreatePickupRequest(values, { reasonIds, otherReasonText }),
      documents,
    });
  };

  const reasonsSlot = (
    <>
      <MultiSelectInput
        id="pickup-request-reasons"
        title={t("pickupRequests.createDialog.reasonsLabel")}
        placeholder={reasonsPlaceholder}
        value={reasonIds}
        options={reasonOptions}
        isLoading={reasonsQuery.isLoading}
        disabled={submitResult.isLoading}
        isError={!!fieldServerErrors.reasonIds}
        errorText={fieldServerErrors.reasonIds}
        onChange={handleReasonsChange}
      />
      {isOtherReasonSelected && (
        <TextInput
          required
          id="pickup-request-other-reason"
          title={t("pickupRequests.createDialog.otherReasonLabel")}
          value={otherReasonText}
          maxLength={OTHER_REASON_MAX_LENGTH}
          disabled={submitResult.isLoading}
          isError={!!(otherReasonError || fieldServerErrors.otherReasonText)}
          errorText={otherReasonError || fieldServerErrors.otherReasonText}
          hintText={t("pickupRequests.form.characterCount", {
            count: otherReasonText.length,
            limit: OTHER_REASON_MAX_LENGTH,
          })}
          onChange={(_event, newValue) => {
            setOtherReasonText(newValue);
            setOtherReasonError(null);
          }}
          onBlur={handleOtherReasonBlur}
          wrapperClassName={styles.formField}
        />
      )}
    </>
  );

  const documentsSlot = (
    <div className={styles.section}>
      <span className={styles.subheader}>
        {t("pickupRequests.createDialog.documentsTitle")}
      </span>
      <FileUploader
        isErrorDismiss
        id="pickup-request-documents-input"
        files={uploadedFiles}
        errors={documentErrors}
        bannerTitle="pickupRequests.createDialog.documentsRejectedTitle"
        hintText={t("pickupRequests.createDialog.documentsHint")}
        accept={ALLOWED_DOCUMENT_EXTENSIONS.join(",")}
        disabled={submitResult.isLoading}
        isUploadHidden={!canManageDocuments}
        uploadProgressData={NO_UPLOAD_PROGRESS}
        onDrop={handleFilesDropped}
        onRemove={removeDocument}
        onErrorDismiss={() => setDocumentErrors([])}
        onDescriptionChanged={changeDocumentDescription}
      />
    </div>
  );

  return (
    <Dialog
      modal
      width={528}
      title={t(
        isEdit
          ? "pickupRequests.createDialog.editTitle"
          : "pickupRequests.createDialog.title"
      )}
      isActive={isActive}
      onClose={onClose}
    >
      {submitResult.isLoading && <Loader isBlocking />}
      {submitResult.isError && !Object.keys(fieldServerErrors).length && (
        <ApiErrorBanner
          id="create-pickup-request-error-banner"
          requestStatus={submitResult}
        />
      )}
      <PickupRequestForm
        collectorOptions={collectorOptions}
        collectorSearchText={collectorSearchText}
        onCollectorSearchTextChange={setCollectorSearchText}
        haulerOptions={haulerOptions}
        haulerOptionsCollectorId={formValues.collectorId}
        carrierOptions={carrierOptions}
        materialTypes={materialTypes}
        isCollectorsLoading={collectors.isLoading}
        isMaterialsLoading={isMaterialsLoading}
        isCollectorLocked={isCollectorLocked}
        lockedCollectorAsSelect={!!lockedCollector}
        submitLabel={
          isEdit ? t("pickupRequests.createDialog.editSubmitLabel") : undefined
        }
        value={formValues}
        onChange={setFormValues}
        onSubmit={handleSubmit}
        onCancel={onClose}
        isSubmitting={submitResult.isLoading}
        isSubmitBlocked={isOtherReasonMissing || (isEdit && !request)}
        serverErrors={fieldServerErrors}
        reasonsSlot={showReasons ? reasonsSlot : undefined}
        documentsSlot={documentsSlot}
        showPortalOnlyFields={showPortalOnlyFields}
        isAssignmentLocked={isAssignmentLocked}
      />
    </Dialog>
  );
};

export default PickupRequestDialog;
